import {
    Box,
    CircularProgress,
    IconButton,
    Paper,
    Popover,
    Stack,
    Typography
} from '@mui/material';
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
    getNotificationCategory,
    getNotificationDescription,
} from '../../utils/notifications';

const clinicianTypeLabelMap = {
    NURSING: 'Enfermagem',
    CARDIOLOGY: 'Cardiologia',
    GERIATRICS: 'Geriatria',
    NUTRITION: 'Nutrição',
    NEUROLOGY: 'Neurologia',
    PSYCHIATRY: 'Psiquiatria',
    PSYCHOLOGY: 'Psicologia',
    PHYSICAL_THERAPY: 'Fisioterapia',
    OCCUPATIONAL_THERAPY: 'Terapia Ocupacional',
    SOCIAL_ASSISTANT: 'Assistente Social',
    GENERAL_PRACTICE: 'Medicina Geral'
};

const renderNotificationMarker = (notification) => {
    const type = (notification?.type || '').toUpperCase();

    if (type === 'ALERT') {
        return (
            <Box
                component="img"
                src="/assets/logos-svg/alert.svg"
                alt="Alerta"
                sx={{ width: 19, height: 19, display: 'block' }}
            />
        );
    }

    if (type === 'MISSED_CALL') {
        return (
            <Box
                component="img"
                src="/assets/logos-svg/missedCall.svg"
                alt="Chamada perdida"
                sx={{ width: 19, height: 19, display: 'block' }}
            />
        );
    }

    if (type === 'APPT_REMINDER') {
        return (
            <Box
                component="img"
                src="/assets/logos-svg/agendaRed.svg"
                alt="Lembrete de agendamento"
                sx={{ width: 19, height: 19, display: 'block' }}
            />
        );
    }

    if (type === 'NEW_DOCUMENT_UPLOAD') {
        return (
            <Box
                component="img"
                src="/assets/logos-svg/uploadRed.svg"
                alt="Novo documento"
                sx={{ width: 19, height: 19, display: 'block' }}
            />
        );
    }

    return <FiberManualRecordIcon sx={{ fontSize: 19, color: '#3D3D3D' }} />;
};

const NotificationsPopover = ({
    anchorEl,
    open,
    notifications = [],
    loading = false,
    onClose,
    onMarkSeen,
    onNotificationClick,
    onPrevPage,
    onNextPage,
    disablePrev = false,
    disableNext = false,
    pageLabel = ''
}) => (
    <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
            sx: {
                mt: 1.2,
                width: 620,
                maxWidth: 'calc(100vw - 24px)',
                borderRadius: '28px',
                p: 1.5,
                bgcolor: '#FFFFFF',
                boxShadow: '0px 16px 42px rgba(61, 61, 61, 0.18)',
                border: '1px solid #ECECEC'
            }
        }}
    >
        <Stack spacing={1.4}>
            {loading ? (
                <Paper
                    sx={{
                        minHeight: 140,
                        borderRadius: '22px',
                        bgcolor: '#F7F7F7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: 'none'
                    }}
                >
                    <Stack alignItems="center" spacing={1.25}>
                        <CircularProgress size={28} sx={{ color: '#D63731' }} />
                        <Typography variant="body2" color="text.secondary">
                            A carregar notificações...
                        </Typography>
                    </Stack>
                </Paper>
            ) : null}

            {!loading && notifications.length === 0 ? (
                <Paper
                    sx={{
                        minHeight: 140,
                        borderRadius: '22px',
                        bgcolor: '#F7F7F7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: 'none',
                        px: 2
                    }}
                >
                    <Typography variant="body1" fontWeight={600} color="#6F6F6F">
                        Sem notificações pendentes
                    </Typography>
                </Paper>
            ) : null}

            {!loading
                ? notifications.map((notification) => {
                      const category = getNotificationCategory(notification);
                      const subCategory =
                          notification?.data?.clinician_type &&
                          notification.data.clinician_type !== 'UNKNOWN'
                              ? clinicianTypeLabelMap[notification.data.clinician_type] ||
                                notification.data.clinician_type
                              : '';

                      return (
                          <Paper
                              key={notification.uuid}
                              onClick={() => onNotificationClick?.(notification)}
                              sx={{
                                  px: 2.2,
                                  py: 1.7,
                                  borderRadius: '22px',
                                  bgcolor: '#F7F7F7',
                                  boxShadow: 'none',
                                  display: 'grid',
                                  gridTemplateColumns: '28px minmax(0, 180px) minmax(0, 1fr) 56px',
                                  alignItems: 'center',
                                  gap: 1.5,
                                  cursor: onNotificationClick ? 'pointer' : 'default',
                                  transition: 'background-color 0.2s ease',
                                  '&:hover': onNotificationClick
                                      ? {
                                            bgcolor: '#EEEEEE'
                                        }
                                      : undefined
                              }}
                          >
                              {renderNotificationMarker(notification)}

                              <Box sx={{ minWidth: 0 }}>
                                  <Typography
                                      variant="body1"
                                      fontWeight={700}
                                      color="#111111"
                                      sx={{ lineHeight: 1.15 }}
                                  >
                                      {category}
                                  </Typography>
                                  {subCategory ? (
                                      <Typography
                                          variant="body2"
                                          fontWeight={700}
                                          color="#111111"
                                          sx={{ lineHeight: 1.15 }}
                                      >
                                          {subCategory}
                                      </Typography>
                                  ) : null}
                              </Box>

                              <Typography
                                  variant="body1"
                                  color="#8A9AA5"
                                  sx={{
                                      textAlign: 'center',
                                      lineHeight: 1.35,
                                      minWidth: 0
                                  }}
                              >
                                  {getNotificationDescription(notification)}
                              </Typography>

                              <IconButton
                                  onClick={(event) => {
                                      event.stopPropagation();
                                      onMarkSeen?.(notification);
                                  }}
                                  sx={{
                                      width: 44,
                                      height: 44,
                                      justifySelf: 'end',
                                      bgcolor: '#3D3D3D',
                                      color: '#FFFFFF',
                                      '&:hover': {
                                          bgcolor: '#2B2B2B'
                                      }
                                  }}
                              >
                                  <DoneOutlinedIcon sx={{ fontSize: 22 }} />
                              </IconButton>
                          </Paper>
                      );
                  })
                : null}

            {!loading && (notifications.length > 0 || !disablePrev || !disableNext) ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 0.4, gap: 0.5 }}>
                    <IconButton size="small" onClick={onPrevPage} disabled={disablePrev}>
                        <KeyboardArrowLeftIcon fontSize="small" />
                    </IconButton>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ minWidth: 28, textAlign: 'center', alignSelf: 'center' }}
                    >
                        {pageLabel}
                    </Typography>
                    <IconButton size="small" onClick={onNextPage} disabled={disableNext}>
                        <KeyboardArrowRightIcon fontSize="small" />
                    </IconButton>
                </Box>
            ) : null}
        </Stack>
    </Popover>
);

export default NotificationsPopover;

